"use client";

import { useMemo } from "react";
import {
  syncResult,
  type SyncQueryResult,
} from "@/types/async-protocol";
import {
  LiveFlashKind,
  type LiveFlashEntry,
  type LiveFlashesByKind,
  type MemberActivityHint,
} from "@/types/frontend-types";
import {
  useSessionDataContext,
  useSessionLiveContext,
} from "./context";
import {
  selectProductChangesValue,
  selectProductReadStateValue,
  type ProductChangesValue,
  type ProductReadStateValue,
} from "./hook-selectors";

export type {
  ProductChangesValue,
  ProductReadStateValue,
} from "./hook-selectors";

export interface LiveFlashValue {
  entry: LiveFlashEntry | undefined;
  isFlashing: boolean;
  fields: Set<string> | undefined;
  hasField: (field: string) => boolean;
}

/**
 * Read the current live-flash entry for any (kind, entityId) pair. Returns
 * isFlashing=false when no entry is active. The entry auto-clears after
 * LIVE_FLASH_MS from the provider's timeout map; consumers don't need to
 * manage their own timers.
 */
export function useLiveFlash(
  kind: LiveFlashKind,
  entityId: string | null | undefined,
): SyncQueryResult<LiveFlashValue> {
  const { liveFlashes } = useSessionLiveContext();

  const value = useMemo<LiveFlashValue>(() => {
    const entry = entityId ? liveFlashes[kind][entityId] : undefined;
    return {
      entry,
      isFlashing: entry != null,
      fields: entry?.fields,
      hasField: (field: string): boolean => entry?.fields.has(field) ?? false,
    };
  }, [liveFlashes, kind, entityId]);

  return syncResult(value);
}

/**
 * Return the entire flash map for a given kind. Components that render a
 * list of entities (e.g. version tabs) consume this once and look up each
 * id by membership, since `useLiveFlash` can't be called inside a loop.
 */
export function useLiveFlashesByKind(
  kind: LiveFlashKind,
): SyncQueryResult<LiveFlashesByKind> {
  const { liveFlashes } = useSessionLiveContext();
  return syncResult(liveFlashes[kind]);
}

/**
 * Return the most-recent activity hint for a member chip, or undefined.
 * Auto-clears after MEMBER_ACTIVITY_MS from the provider's timeout map.
 */
export function useMemberActivity(
  memberId: string | null | undefined,
): SyncQueryResult<MemberActivityHint | undefined> {
  const { memberActivity } = useSessionLiveContext();
  const value = useMemo<MemberActivityHint | undefined>(() => {
    if (!memberId) return undefined;
    return memberActivity[memberId];
  }, [memberActivity, memberId]);
  return syncResult(value);
}

export function useProductChanges(
  productId: string | null | undefined,
): SyncQueryResult<ProductChangesValue> {
  const productFlash = useLiveFlash(LiveFlashKind.Product, productId).data;
  const productFlashFields = productFlash.fields;
  const productFlashIsFlashing = productFlash.isFlashing;

  const value = useMemo(
    () =>
      selectProductChangesValue({
        fields: productFlashFields,
        isFlashing: productFlashIsFlashing,
      }),
    [productFlashFields, productFlashIsFlashing],
  );

  return syncResult(value);
}

export function useProductReadState(
  productId: string | null = null,
): SyncQueryResult<ProductReadStateValue> {
  const context = useSessionDataContext();

  return syncResult(selectProductReadStateValue({
    productId,
    mainChatScopeKey: context.mainChatScopeKey,
    productReads: context.productReads,
    unreadCollabCounts: context.unreadCollabCounts,
    productsSeen: context.productsSeen,
  }));
}
